import { useUpdateCheckbox } from 'pages/ListerVehicleUploadV2/hooks/useUpdateCheckbox';
import React, { useEffect, useRef } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { NotesStatusIcon } from './NotesStatusIcon';
import { useFinalReviewDialogOpened } from 'services/listerUploadVehicle/v2';
import { AiNotesDialogComponent } from 'pages/Admin/VehicleReview/VehiclePictures/components/addNotesDialog/AiComponent';
import { OriginalText } from 'pages/Admin/VehicleReview/VehiclePictures/components/addNotesDialog/OriginalText';
import GptIcon from 'components/Icons/gpt.svg';

type Props = {
  notesRef?: React.RefObject<HTMLTextAreaElement>;
  fieldName: string;
  sectionId: string;
  index?: number;
  autosave: boolean;
  withAi: boolean;
};

const NotesWrapper = styled.div<{ hasAiIcon: boolean }>`
  border-radius: 20px;
  border-top-left-radius: 0;
  border: 2px solid #3994de;
  display: flex;
  padding: 8px 40px 7px 12px;
  position: relative;

  ${props =>
    props.hasAiIcon &&
    `
        padding: 8px 40px 7px 30px;
        background: #F0F0F0;
        border: 2px solid #3994DE00;

        & > textarea {
            background: transparent;
        }
    `}
`;

const NotesInput = styled.textarea`
  border: none;
  font-size: 12px;
  color: #000;
  resize: none;
  width: 100%;
  font-family: Roboto;
`;

const OriginalTextLabel = styled.div`
  border: 2px solid #cccccc;
  border-top-left-radius: 0px;
  border-radius: 20px;
  font-size: 12px;
`;

const GptIconWrapper = styled.div`
  position: absolute;
  left: 8px;
  top: 11px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CheckboxNotesComponent: React.FC<Props & { messageField: string; aiIcon: boolean }> = ({
  autosave,
  notesRef,
  fieldName,
  sectionId,
  index,
  messageField,
  aiIcon
}) => {
  const { input: notesInput, meta: notesMeta } = useField(`${fieldName}.notes.${messageField}`);

  const {
    input: { value: vehicleId }
  } = useField(`vehicleId`);

  const {
    input: { value: serverId }
  } = useField(`${sectionId}.id`);

  const {
    input: { value: checkboxId }
  } = useField(`${fieldName}.id`);

  const finalReviewOpen = useFinalReviewDialogOpened();

  const lastSavedValue = useRef(notesInput.value);

  useEffect(() => {
    if (!autosave || !notesMeta.dirty || finalReviewOpen) return;

    if (notesInput.value === lastSavedValue.current) return;

    const update = () => {
      updateCheckboxNotes({ notes: notesInput.value });
      lastSavedValue.current = notesInput.value;
    };

    const debouncedUpdate = setTimeout(() => {
      update();
    }, 1000);

    return () => clearTimeout(debouncedUpdate);
  }, [notesInput.value, autosave, checkboxId, serverId, vehicleId, finalReviewOpen]);

  const [updateCheckboxNotes, updateRequestStatus] = useUpdateCheckbox(checkboxId, sectionId, index || -1);

  return (
    <NotesWrapper hasAiIcon={aiIcon}>
      {aiIcon && (
        <GptIconWrapper>
          <GptIcon />
        </GptIconWrapper>
      )}
      <NotesInput ref={notesRef} {...notesInput} />
      {autosave && <NotesStatusIcon showInitialCheckmark={!!notesInput.value} status={updateRequestStatus} />}
    </NotesWrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 40px;
`;

export const CheckboxNotes: React.FC<Props> = props => {
  const notesFieldId = `${props.fieldName}.notes`;
  const {
    input: { value: status }
  } = useField(`${notesFieldId}.status`, { subscription: { value: true } });

  const { notesRef, ...restProps } = props;

  if (!props.withAi) {
    return (
      <Wrapper>
        <CheckboxNotesComponent {...props} messageField={'message'} aiIcon={false} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {status === 'ai_used' && <OriginalText fieldId={notesFieldId} />}
      <CheckboxNotesComponent {...props} messageField={'message'} aiIcon={status === 'ai_used'} />
      {status !== 'ai_used' && <AiNotesDialogComponent noLabel fieldId={notesFieldId} />}
      {status === 'ai_fetched' && <CheckboxNotesComponent {...restProps} aiIcon messageField={'ai_message'} />}
    </Wrapper>
  );
};
